import type { CustosViabilidade } from '@/lib/api/auctions';

export type HomePagePreference = 'dashboard' | 'oportunidades';
export type LanguagePreference = 'pt-BR';
export type CostInputMode = 'percent' | 'fixed';

export type FinancialCostPreference = {
  mode: CostInputMode;
  percent?: number;
  fixed?: number;
};

export type UserPreferencesV1 = {
  version: 1;
  general: {
    homePage: HomePagePreference;
    language: LanguagePreference;
  };
  financial: {
    roiMinimoAceitavelPercent: number;
    valorAdvogado: number;
    custasJudiciais: FinancialCostPreference;
    comissaoLeiloeiro: FinancialCostPreference;
    custoCorretor: FinancialCostPreference;
    custoIrpf: FinancialCostPreference;
    reparosReformas: FinancialCostPreference;
    custosCartorio: number;
    custoOportunidade: {
      enabled: boolean;
      percent?: number;
    };
  };
};

export const DEFAULT_USER_PREFERENCES: UserPreferencesV1 = {
  version: 1,
  general: {
    homePage: 'dashboard',
    language: 'pt-BR',
  },
  financial: {
    roiMinimoAceitavelPercent: 30,
    valorAdvogado: 0,
    custasJudiciais: { mode: 'percent', percent: 2.5 },
    comissaoLeiloeiro: { mode: 'percent', percent: 5 },
    custoCorretor: { mode: 'percent', percent: 5 },
    custoIrpf: { mode: 'percent', percent: 15 },
    reparosReformas: { mode: 'percent', percent: 10 },
    custosCartorio: 0,
    custoOportunidade: { enabled: false, percent: 0 },
  },
};

export type UserPreferencesResponse = {
  preferences: UserPreferencesV1;
  userOverrides: Record<string, unknown>;
  systemDefaults: UserPreferencesV1;
};

export function homePagePathFromPreference(homePage: HomePagePreference): string {
  return homePage === 'oportunidades' ? '/oportunidades' : '/dashboard';
}

function applyCostPreference(
  pref: FinancialCostPreference,
  percentKey: keyof CustosViabilidade,
  fixedKey: keyof CustosViabilidade,
  out: Partial<CustosViabilidade>
) {
  if (pref.mode === 'percent') {
    (out as Record<string, number | undefined>)[percentKey as string] = pref.percent ?? 0;
    (out as Record<string, unknown>)[fixedKey as string] = undefined;
  } else {
    (out as Record<string, number | undefined>)[fixedKey as string] = pref.fixed ?? 0;
    (out as Record<string, unknown>)[percentKey as string] = undefined;
  }
}

/** Converte preferências financeiras em defaults para `CustosViabilidade`. */
export function financialPreferencesToCustosDefaults(
  financial: UserPreferencesV1['financial']
): Partial<CustosViabilidade> {
  const out: Partial<CustosViabilidade> = {
    roiMinimoAceitavelPercent: financial.roiMinimoAceitavelPercent,
    valorAdvogado: financial.valorAdvogado,
    custosCartorio: financial.custosCartorio,
  };

  applyCostPreference(
    financial.custasJudiciais,
    'custasJudiciaisPercent',
    'custasJudiciaisValor',
    out
  );
  applyCostPreference(
    financial.comissaoLeiloeiro,
    'comissaoLeiloeiroPercent',
    'comissaoLeiloeiroValor',
    out
  );
  applyCostPreference(financial.custoCorretor, 'custoCorretorPercent', 'custoCorretorValor', out);
  applyCostPreference(financial.custoIrpf, 'custoIrpfPercent', 'custoIrpfValor', out);
  applyCostPreference(
    financial.reparosReformas,
    'custosReparosReformasPercent',
    'custosReparosReformas',
    out
  );

  if (financial.custoOportunidade.enabled) {
    out.custosOportunidadePercent = financial.custoOportunidade.percent ?? 0;
  } else {
    out.custosOportunidadePercent = undefined;
    out.custosOportunidade12Meses = undefined;
  }

  return out;
}
